import React, { useState } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export default function DeviceFeedbackTable({ feedback }) {
  const [copiedField, setCopiedField] = useState(null);
  
  if (!feedback) return <div>No feedback data available.</div>;

  const { last_iteration, raw, extraHex, ...summary } = feedback || {};

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatValue = (key, value) => {
    // Skip raw and extraHex (handled separately)
    if (key === 'raw' || key === 'extraHex') return null;
    
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (typeof value === 'number') {
      // Format numbers nicely
      return value.toLocaleString();
    }
    return String(value);
  };

  // Render key-value summary (excluding last_iteration, raw, extraHex)
  const rows = Object.entries(summary)
    .filter(([key]) => key !== 'raw' && key !== 'extraHex')
    .map(([key, value]) => {
      const displayValue = formatValue(key, value);
      if (displayValue === null) return null;
      
      return (
        <tr key={key}>
          <td style={{ 
            fontWeight: 'bold', 
            padding: '4px 8px', 
            background: '#222', 
            color: '#fff',
            width: '200px',
            verticalAlign: 'top'
          }}>{key}</td>
          <td style={{ 
            padding: '4px 8px', 
            background: '#111', 
            color: '#fff',
            wordBreak: 'break-word',
            maxWidth: '600px'
          }}>{displayValue}</td>
        </tr>
      );
    })
    .filter(Boolean);

  // Render per-element combined results if present
  const elements = last_iteration?.elements || [];
  const parsedCount = last_iteration?.element_count ?? elements.length;
  const requestedCount = last_iteration?.requested_count ?? parsedCount;

  const renderHexField = (label, hexData) => {
    if (!hexData) return null;
    const truncated = hexData.length > 80 ? hexData.substring(0, 80) + '...' : hexData;
    
    return (
      <tr>
        <td style={{ 
          fontWeight: 'bold', 
          padding: '4px 8px', 
          background: '#222', 
          color: '#fff',
          width: '200px',
          verticalAlign: 'top'
        }}>{label}</td>
        <td style={{ 
          padding: '4px 8px', 
          background: '#111', 
          color: '#fff',
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={hexData.length > 80 ? hexData : ''}>
              <span style={{ 
                fontFamily: 'monospace', 
                fontSize: '0.85em',
                wordBreak: 'break-all',
                maxWidth: '500px',
                display: 'inline-block'
              }}>
                {truncated}
              </span>
            </Tooltip>
            <Tooltip title={copiedField === label ? 'Copied!' : 'Copy full value'}>
              <IconButton 
                size="small" 
                onClick={() => copyToClipboard(hexData, label)}
                sx={{ color: copiedField === label ? '#4caf50' : '#fff', ml: 1 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <div style={{ fontSize: '0.75em', color: '#888', marginTop: '4px' }}>
            {hexData.length} characters
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#111', color: '#fff', marginTop: '1em', tableLayout: 'fixed' }}>
        <tbody>
          {rows}
          {renderHexField('raw', raw)}
          {renderHexField('extraHex', extraHex)}
        </tbody>
      </table>
      {elements.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            Last Iteration Results ({parsedCount}{requestedCount ? ` / requested ${requestedCount}` : ''})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#111', color: '#fff' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', background: '#222' }}>No.</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', background: '#222' }}>Symbol</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', background: '#222' }}>Target V</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', background: '#222' }}>Achieved V</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', background: '#222' }}>DAC</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', background: '#222' }}>Iterations</th>
                <th style={{ textAlign: 'center', padding: '6px 8px', background: '#222' }}>Converged</th>
              </tr>
            </thead>
            <tbody>
              {elements.map((el, idx) => (
                <tr key={idx}>
                  <td style={{ padding: '4px 8px', borderTop: '1px solid #333' }}>{idx + 1}</td>
                  <td style={{ padding: '4px 8px', borderTop: '1px solid #333' }}>{el.symbol}</td>
                  <td style={{ padding: '4px 8px', borderTop: '1px solid #333', textAlign: 'right' }}>{Number(el.target_voltage_final).toFixed(3)}</td>
                  <td style={{ padding: '4px 8px', borderTop: '1px solid #333', textAlign: 'right' }}>{Number(el.achieved_voltage_final).toFixed(3)}</td>
                  <td style={{ padding: '4px 8px', borderTop: '1px solid #333', textAlign: 'right' }}>{el.dac_value}</td>
                  <td style={{ padding: '4px 8px', borderTop: '1px solid #333', textAlign: 'right' }}>{el.iterations}</td>
                  <td style={{ padding: '4px 8px', borderTop: '1px solid #333', textAlign: 'center' }}>{el.converged ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}